import { useState, useCallback } from 'react';
import { ApiClient } from '../services/apiClient';
import { logger } from '../logger';

export const useChat = () => {
  const [messages, setMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [lastQuestionNo, setLastQuestionNo] = useState(null);

  const sendMessage = useCallback(async (project, question, userId) => {
    if (!question?.trim()) return;
    setError(null);

    const userMsg = {
      id: crypto?.randomUUID?.() || `${Date.now()}-u`,
      type: 'user',
      content: question.trim(),
      timestamp: Date.now()
    };
    setMessages((prev) => {
      const next = [...prev, userMsg];
      logger.debug('[useChat] appended user message; total:', next.length, 'last:', next[next.length - 1]);
      return next;
    });

    setIsLoading(true);
    try {
      const payload = {
        project,
        question: question.trim(),
        userid: userId || 'system',
        ...(conversationId ? { conversation_id: conversationId } : {}),
        ...(conversationId && Number.isFinite(lastQuestionNo) ? { question_no: lastQuestionNo + 1 } : {})
      };
      logger.debug('[useChat] calling retrieve with:', payload);

      const res = await ApiClient.retrieve(payload);
      logger.debug('[useChat] retrieve response:', res);

      const botContent = {
        prelude: res.prelude || '',
        // Keep markdown in a.content exactly as received; Markdown component will render it.
        answer: Array.isArray(res.answer) ? res.answer.map(a => ({
          heading: a.heading || '',
          content: a.content || '',     // markdown retained
          citation: a.citation || ''
        })) : [],
        Suggestions: Array.isArray(res.Suggestions) ? res.Suggestions : []
      };

      const botMsg = {
        id: crypto?.randomUUID?.() || `${Date.now()}-a`,
        type: 'bot',
        content: botContent,
        timestamp: Date.now(),
        meta: {
          conversationId: res.conversation_id,
          questionNo: res.question_no,
          project
        }
      };

      if (res.conversation_id) setConversationId(res.conversation_id);
      if (Number.isFinite(res.question_no)) setLastQuestionNo(res.question_no);
      logger.debug('[useChat] constructed bot message:', botMsg);

      setMessages((prev) => {
        const next = [...prev, botMsg];
        logger.debug('[useChat] appended bot message; total:', next.length, 'last:', next[next.length - 1]);
        return next;
      });
    } catch (e) {
      logger.error('[useChat] retrieve error:', e);
      const isTimeout = e && (e.code === 'ETIMEDOUT' || e.name === 'TimeoutError');
      setError(isTimeout ? 'Request timed out. Please try again.' : (e.message || 'Request failed'));

      if (!isTimeout) {
        const errMsg = {
          id: crypto?.randomUUID?.() || `${Date.now()}-e`,
          type: 'error',
          content: 'There was an error retrieving the answer.',
          timestamp: Date.now()
        };
        setMessages((prev) => {
          const next = [...prev, errMsg];
          logger.debug('[useChat] appended error message; total:', next.length, 'last:', next[next.length - 1]);
          return next;
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, lastQuestionNo]);

  const loadConversation = useCallback(async (project, userId, clickedId) => {
    if (!clickedId || !project || !userId) return;
    logger.debug('[useChat] loadConversation clicked:', { clickedId, project, userId });
    setIsLoading(true);
    setError(null);
    try {
      const normProject = typeof project === 'object'
        ? (project.id || project.project || project.code || project.name || project)
        : project;

      const res = await ApiClient.getConversationById({
        project: normProject,
        user: userId.toLowerCase(),           // changed back from userid
        conversation_id: clickedId
      });

      const logs = Array.isArray(res?.logs) ? res.logs.slice() : [];
      if (!logs.length) {
        logger.warn('[useChat] No logs returned; keeping existing messages.');
        setConversationId(clickedId);
        setLastQuestionNo(null);
        return;
      }

      // Ensure chronological order by question_no if present
      logs.sort((a, b) => {
        const qa = Number(a.question_no);
        const qb = Number(b.question_no);
        if (Number.isFinite(qa) && Number.isFinite(qb)) return qa - qb;
        return 0;
      });

      const rebuilt = [];
      logs.forEach((log, idx) => {
        // User question
        rebuilt.push({
          id: crypto?.randomUUID?.() || `${Date.now()}-u${idx}`,
          type: 'user',
          content: log.Title || log.title || log.question || `Question ${log.question_no ?? idx + 1}`,
          timestamp: Date.now()
        });

        // Bot answer
        const answerArray = Array.isArray(log.answer)
          ? log.answer.map(a => ({
              heading: a.heading || '',
              content: a.content || '',
              citation: a.citation || ''
            }))
          : [];

        rebuilt.push({
          id: crypto?.randomUUID?.() || `${Date.now()}-b${idx}`,
          type: 'bot',
            content: {
              prelude: log.prelude || '',
              answer: answerArray,
              Suggestions: Array.isArray(log.Suggestions) ? log.Suggestions : []
            },
            timestamp: Date.now(),
            meta: {
              conversationId: log.conversation_id || clickedId,
              questionNo: log.question_no,
              project: normProject
            }
        });
      });

      setMessages(rebuilt);
      setConversationId(clickedId);
      // Last question number = highest question_no in logs
      const last = logs.reduce((m, l) => {
        const q = Number(l.question_no);
        return Number.isFinite(q) && q > m ? q : m;
      }, 0);
      setLastQuestionNo(last || null);
    } catch (e) {
      logger.error('[useChat] loadConversation error:', e);
      setError(e.message || 'Failed to load conversation');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearChat = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setLastQuestionNo(null);
    setError(null);
  }, []);

  const startNewConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setLastQuestionNo(null);
    setError(null);
  }, []);

  return {
    messages,
    isLoading,
    error,
    sendMessage,
    clearChat,
    startNewConversation,
    loadConversation,
    conversationId
  };
};
